import moment from 'moment';
import { maxPlanEntries, MIN_REST_AMOUNT, PaymentPlanEntryStatus, PaymentPlanEntryTypes, } from '../../../payment-plans/index.js';
import helpers from '../../index.js';
import { addInterval, appendPerEntryFeeVoucher, balanceAfterPlan, createNewNormalEntry, hasValidFeeAmount, } from '../index.js';
/* Per-entry amounts for the amount method: the configured base amount,
   overridden by any scheduled changes effective at the entry date.
   `drawnAmountAt` is what the debtor actually pays per entry: the amount plus
   the per-entry fee when one applies, except on recalculation, which takes the
   amount as an instalment that already includes it. `amountAt` is what of it
   pays the debt. */
export const createInstalmentCalculator = (baseAmount, scheduledAmountChanges, feeStructure, recalculation) => {
    if (typeof baseAmount !== 'number') {
        throw new Error(`Please provide a number, not a ${typeof baseAmount}`);
    }
    if (baseAmount <= 0) {
        throw new Error(`Amount cannot be less or equals 0`);
    }
    const changes = (scheduledAmountChanges || [])
        .slice()
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    for (const change of changes) {
        if (typeof change.amount !== 'number') {
            throw new Error(`Please provide a number, not a ${typeof change.amount}`);
        }
        if (change.amount <= 0) {
            throw new Error(`Scheduled amount cannot be less or equals 0`);
        }
    }
    const perEntryFee = hasValidFeeAmount(feeStructure?.perEntryFee?.amount)
        ? feeStructure.perEntryFee.amount
        : 0;
    const drawnAmountAt = (date) => {
        let effective = baseAmount;
        for (const change of changes) {
            if (!moment(change.date).isAfter(date)) {
                effective = change.amount;
            }
        }
        return recalculation ? effective : effective + perEntryFee;
    };
    const amountAt = (date) => drawnAmountAt(date) - perEntryFee;
    return { perEntryFee, amountAt, drawnAmountAt };
};
export const generateByAmount = async (input) => {
    const { interval, feeStructure, documents, economyPolicy, fixedDistributions, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, conditions, } = input;
    const { perEntryFee, amountAt, drawnAmountAt } = createInstalmentCalculator(input.amount, input.scheduledAmountChanges, feeStructure, input.recalculation);
    const maxEntries = maxPlanEntries(interval) - input.plan.length;
    const entryDates = [input.currDate];
    const entryDate = (index) => {
        while (entryDates.length <= index) {
            entryDates.push(addInterval(interval, entryDates[entryDates.length - 1]));
        }
        return entryDates[index];
    };
    // Instalments from entry `from` on that fit below `debt`, leaving at most
    // one instalment for the entry after them
    const instalmentsBelow = (debt, from) => {
        let count = 0;
        let paid = 0;
        while (from + count < maxEntries - 1) {
            const instalment = amountAt(entryDate(from + count));
            if (instalment <= 0) {
                throw new Error('An instalment must pay more than its per-entry fee');
            }
            if (paid + instalment >= debt) {
                break;
            }
            paid += instalment;
            count++;
        }
        return count;
    };
    // `count` new entries: instalments, then a REST, each with its per-entry fee
    const schedule = (count) => {
        const dates = Array.from({ length: count }, (_, index) => entryDate(index));
        return {
            plan: input.plan.concat(dates.map((date, index) => {
                const entry = createNewNormalEntry(date, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, drawnAmountAt(date));
                return index < count - 1
                    ? entry
                    : { ...entry, type: PaymentPlanEntryTypes.REST, amount: 0 };
            })),
            filteredVouchers: dates.reduce((vouchers, date) => appendPerEntryFeeVoucher(vouchers, feeStructure, date.toISOString()), input.filteredVouchers),
        };
    };
    const projectBalance = ({ plan, filteredVouchers }) => balanceAfterPlan(filteredVouchers, documents, economyPolicy, fixedDistributions, plan, conditions);
    const existingPlanAmount = input.plan.reduce((acc, e) => acc + (e.status !== PaymentPlanEntryStatus.SETTLED ? (e.amount ?? 0) : 0), 0);
    const storedDebt = -helpers.overview.balance(input.filteredVouchers);
    // TODO: Remove legacy expiration when relevant cases have ended
    const hasLegacyExpiration = input.filteredVouchers.some((v) => v.details.legacyInterestExpiration);
    // The instalments pay their own fees, so the stored debt estimates the
    // count; the projection then adds what accrues until the REST
    let count = instalmentsBelow(storedDebt - existingPlanAmount, 0) + 1;
    let scheduled = schedule(count);
    let balance = await projectBalance(scheduled);
    // The REST is at most the instalment due on its date. A plan whose
    // instalments don't outpace the interest stops at maxPlanEntries, its
    // REST taking whatever is left.
    while (-balance > drawnAmountAt(entryDate(count - 1)) && count < maxEntries) {
        // Legacy expiration drops interest as the plan grows, so only grow by one
        count += hasLegacyExpiration
            ? 1
            : instalmentsBelow(-balance - perEntryFee, count - 1);
        scheduled = schedule(count);
        balance = await projectBalance(scheduled);
    }
    // A lone REST after seed entries is only worth adding when they leave
    // debt: it would otherwise pay nothing but its own fee
    const seedBalance = count === 1 && input.plan.length > 0
        ? await projectBalance({
            plan: input.plan,
            filteredVouchers: input.filteredVouchers,
        })
        : null;
    if (seedBalance === 0) {
        return { plan: input.plan, filteredVouchers: input.filteredVouchers };
    }
    if (count === 1 && (seedBalance ?? balance) > 0) {
        throw new Error('Existing payments already exceed the total debt — cannot generate a payment plan');
    }
    let { plan, filteredVouchers } = scheduled;
    // A REST below the minimum on top of its own fee, or one the instalments
    // already overpay, isn't worth an entry: the instalment before it takes it
    // over
    if (count > 1 && -balance - perEntryFee < MIN_REST_AMOUNT) {
        plan = plan.slice(0, -1);
        if (perEntryFee > 0) {
            filteredVouchers = filteredVouchers.slice(0, -1);
        }
        balance = await projectBalance({ plan, filteredVouchers });
        // The plan overshoots when the projection shows less debt than the
        // vouchers the count was estimated from. Drop each instalment the plan
        // still covers the debt without: no interest accrues once the debt is
        // paid, so dropping one lowers the balance by its amount net of its fee.
        const generatedLength = plan.length;
        while (balance > 0 &&
            plan.length > input.plan.length &&
            plan[plan.length - 1].amount - perEntryFee <= balance) {
            balance -= plan[plan.length - 1].amount - perEntryFee;
            plan = plan.slice(0, -1);
            if (perEntryFee > 0) {
                filteredVouchers = filteredVouchers.slice(0, -1);
            }
        }
        // Re-project once, so the last entry absorbs the exact remainder even
        // where a cost ceiling caps the dropped fees
        if (plan.length < generatedLength) {
            balance = await projectBalance({ plan, filteredVouchers });
        }
    }
    // The last entry settles the remainder. Seed entries (upfront, existing
    // ones) keep their agreed amounts.
    if (balance !== 0 && plan.length > input.plan.length) {
        plan[plan.length - 1] = {
            ...plan[plan.length - 1],
            amount: plan[plan.length - 1].amount + -balance,
            type: PaymentPlanEntryTypes.REST,
        };
    }
    return { plan, filteredVouchers };
};
//# sourceMappingURL=amount.js.map